import React from "react";
import {
  CheckCircleFilled
} from "@ant-design/icons";
import { Button, Input, notification } from "antd";
import { connect } from "react-redux";
import { addNewsLetters } from "../../../redux/actions/API/newsLetters";
import { IDataResponse } from "../../../redux/types/API/ApiResponse";

interface IFeaturesProps {
  addNewsLetters: Function;
  addNewsLettersState: IDataResponse;
}

interface IFeaturesState {
  isMobile: boolean;
  planSize: string;
  activeBlock: string;
  email: string;
  loading: boolean;
}

type NotificationType = "success" | "info" | "warning" | "error";

class Features extends React.Component<IFeaturesProps, IFeaturesState> {
  constructor(props: IFeaturesProps) {
    super(props);
  }

  heroRef = React.createRef<HTMLDivElement>();

  state = {
    isMobile: false,
    planSize: "yearly",
    activeBlock: "silver",
    email: "",
    loading: false,
  };

  openNotificationWithIcon = (type: NotificationType, message: string) => {
    notification.open({
      message: message,
      type: type,
      duration: 3,
    });
  };

  private scrollAnimationFrame: number | null = null;

  componentDidMount() {
    this.updateIsMobile();
    window.addEventListener("resize", this.updateIsMobile);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateIsMobile);
    if (this.scrollAnimationFrame) {
      cancelAnimationFrame(this.scrollAnimationFrame);
    }
  }

  componentDidUpdate(
    prevProps: Readonly<IFeaturesProps>,
    prevState: Readonly<IFeaturesState>
  ): void {
    if (
      prevProps.addNewsLettersState?.loading &&
      !this.props.addNewsLettersState?.loading
    ) {
      if (this.props.addNewsLettersState?.error) {
        this.openNotificationWithIcon(
          "error",
          this.props.addNewsLettersState?.error?.message
        );
        this.setState({ loading: false });
      } else {
        this.setState({ loading: false, email: "" });
        this.openNotificationWithIcon("success", "Thank you for subscribing!");
      }
    }
  }

  handleNewsletterSubscribe = () => {
    const { email } = this.state;
    const { addNewsLetters } = this.props;

    if (!email) {
      this.openNotificationWithIcon(
        "error",
        "Please enter your email address."
      );
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      this.openNotificationWithIcon("error", "Please enter a valid email address.");
      return;
    }

    this.setState({ loading: true });
    const payload = {
      email,
      pageId: "home",
      ip_address: null,
      status: 1,
    };

    addNewsLetters(payload);
  };

  updateIsMobile = () => {
    this.setState({ isMobile: window.innerWidth <= 767 });
  };

  renderBullet(text: string, isActive: boolean) {
    return (
      <li className="flex items-start gap-2">
        <CheckCircleFilled
          className={`flex-shrink-0 mt-1 text-lg ${isActive ? "text-white" : "text-[#207af0]"
            }`}
        />
        <span className="text-sm break-words leading-snug">{text}</span>
      </li>
    );
  }

  render() {
    return (
      <>
        {/* Main Pricing Section */}
        <div className="bg-white pt-8 pb-0 px-4 sm:px-6 lg:px-8 -mt-20">
          <div className="max-w-6xl mx-auto flex flex-col justify-center items-center text-center my-12">
            {/* Header */}
            <div className="mb-8 px-4 sm:px-6 lg:pl-24 md:pl-20">
              <h3 className="text-[23xl] sm:text-[23xl] lg:text-[27px] xl:text-[27px] font-semibold text-gray-900 mb-4">
                The Right Plan for Smarter Manufacturing
              </h3>
              Simplify operations, gain real-time visibility, and grow your business with a plan that fits your needs.
            </div>

            {/* Plan Toggle */}
            <div className="flex justify-center items-center mb-12 px-4 sm:px-6 lg:pl-24 md:pl-20 gap-0">
              <button
                onClick={() => this.setState({ planSize: "quarterly" })}
                className={`px-6 py-2 rounded-md text-sm font-medium border 
                  ${this.state.planSize === "quarterly"
                    ? "text-[#207af0] border-[#207af0] "
                    : "text-black  font-normal"
                  }`}
              >
                Quarterly
              </button>

              <button
                onClick={() => this.setState({ planSize: "yearly" })}
                className={`px-6 py-2 rounded-md text-sm font-medium border 
                  ${this.state.planSize === "yearly"
                    ? "text-[#207af0] border-[#207af0] "
                    : " text-black font-normal"
                  }`}
              >
                Yearly
              </button>
            </div>

            {/* Pricing Cards */}
            <div className="w-full max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 px-4 sm:px-6">

              {/* Free Plan */}
              <div
                className={`${this.state.activeBlock === "free"
                  ? "bg-blue-600 text-white transform -translate-y-2 shadow-2xl border-[#207af0]"
                  : "bg-white text-gray-900 shadow-lg border-gray-200 hover:shadow-xl hover:-translate-y-1"
                  } border-2 rounded-2xl p-4 sm:p-6 cursor-pointer transition-all duration-300 relative w-full`}
                onClick={() => {
                  this.setState({ activeBlock: "free" });
                }}
              >
                <div className="text-center">
                  <h6
                    className={`text-left text-lg font-semibold mb-2 ${this.state.activeBlock === "free"
                      ? "text-white"
                      : "text-gray-900"
                      }`}
                  >
                    Free
                  </h6>
                  <div
                    className={`text-left text-xl font-bold mb-2 ${this.state.activeBlock === "free"
                      ? "text-white"
                      : "text-blue-[#207af0]"
                      }`}
                  >
                    Free
                  </div>
                  <p
                    className={`text-left text-sm mb-6 ${this.state.activeBlock === "free"
                      ? "text-blue-100"
                      : "text-gray-600"
                      }`}
                  >
                    Unlimited Invoicing
                  </p>
                </div>

                <ul className="space-y-3 mb-8">
                  {this.renderBullet("Single User", this.state.activeBlock === "free")}
                  {this.renderBullet("B2C and B2B Billing", this.state.activeBlock === "free")}
                  {this.renderBullet("Create Quotations", this.state.activeBlock === "free")}
                  {this.renderBullet("Analytics and Reports", this.state.activeBlock === "free")}
                </ul>

                <Button
                  type={
                    this.state.activeBlock === "free" ? "default" : "primary"
                  }
                  className="w-full h-12 rounded-full font-medium transition-all"
                  onClick={() => {
                    window.location.href = "/requestdemo";
                  }}
                >
                  Request a demo
                </Button>
              </div>

              {/* Silver Plan */}
              <div
                className={`${this.state.activeBlock === "silver"
                  ? "bg-[#207af0] text-white transform -translate-y-2 shadow-2xl border-[#207af0]"
                  : "bg-white text-gray-900 shadow-lg border-gray-200 hover:shadow-xl hover:-translate-y-1"
                  } border-2 rounded-2xl p-4 sm:p-6 cursor-pointer transition-all duration-300 relative w-full`}
                onClick={() => {
                  this.setState({ activeBlock: "silver" });
                }}
              >
                <div className="text-center">
                  <h6
                    className={`text-left text-lg font-semibold mb-2 ${this.state.activeBlock === "silver"
                      ? "text-white"
                      : "text-gray-900"
                      }`}
                  >
                    Silver
                  </h6>
                  {/* <div
                    className={`text-lg sm:text-lg lg:text-lg font-bold mb-2 ${this.state.activeBlock === "silver"
                      ? "text-white"
                      : "#207af0"
                      }`}
                  >
                    <span className="inline">
                      {this.state.planSize === "quarterly"
                        ? "20,000 INR"
                        : "5,000 INR"}
                    </span>
                    <span className="inline text-lg sm:text-lg font-fold ">
                      {this.state.planSize === "quarterly"
                        ? "/quarter"
                        : "/month"}
                    </span>
                  </div> */}
                  <div className="flex space-x-1 items-start">
                    <span className={`text-[25px] font-semibold ${this.state.activeBlock === "silver" ? "text-[#ffffff]" : "text-[#1780fb]"}`}>Rs</span>
                    <span className={`text-[55px] font-bold ${this.state.activeBlock === "silver" ? "text-[#ffffff]" : "text-[#1780fb]"} leading-none`}>{this.state.planSize === "quarterly" ? 2397 : 599}</span>
                    <span className={`text-[25px] font-semibold ${this.state.activeBlock === "silver" ? "text-[#ffffff]" : "text-[#1780fb]"}`}>.00</span>
                  </div>
                  <div className="flex flex-col items-end">
                    <div className="relative w-fit inline-block">
                      <div className="flex items-center space-x-1">
                        <span className={`text-[20px] font-semibold ${this.state.activeBlock === "silver" ? "text-[#ffffff]" : ""}`}>{this.state.planSize === "quarterly" ? 3999 : 999}</span>
                        <span className={`text-[20px] font-semibold ${this.state.activeBlock === "silver" ? "text-[#ffffff]" : ""}`}>Rs</span>
                        <span className="text-[20px] text-red-500">*</span>
                      </div>

                      {/* perfect center strike for 20px text */}
                      <span
                        className="absolute left-0 right-0
											 top-1/2 -translate-y-1/2
											  h-[3px] bg-[#ff7a7a]
											   rotate-[-10deg] rounded-full"
                      ></span>
                    </div>
                    <span className={`text-[14px] ${this.state.activeBlock === "silver" ? "text-[#ffffff]" : ""}`}>
                      / user / {this.state.planSize === "quarterly" ? "quarter" : "month"}
                    </span>
                  </div>
                  <p
                    className={`text-left text-sm mb-6 ${this.state.activeBlock === "silver"
                      ? "text-blue-100"
                      : "text-gray-600"
                      }`}
                  >
                    Billed{" "}
                    {this.state.planSize === "yearly" ? "Yearly" : "Quarterly"}
                  </p>
                </div>

                <ul className="space-y-3 mb-8">
                  {/* {this.renderBullet("Upto 5 Users", this.state.activeBlock === "silver")} */}
                  {this.renderBullet("Manage Sales and Purchase", this.state.activeBlock === "silver")}
                  {this.renderBullet("Inventory Management", this.state.activeBlock === "silver")}
                  {this.renderBullet("Sales and Purchase Forecasting", this.state.activeBlock === "silver")}
                  {this.renderBullet("Dashboard and Reports", this.state.activeBlock === "silver")}
                  {this.renderBullet("Reminder and Alerts", this.state.activeBlock === "silver")}
                </ul>
                <Button
                  type={
                    this.state.activeBlock === "silver" ? "default" : "primary"
                  }
                  className="w-full h-12 rounded-full font-medium transition-all"
                  onClick={() => {
                    window.location.href = "/requestdemo";
                  }}
                >
                  Request a demo
                </Button>
              </div>

              {/* Gold Plan */}
              <div
                className={`${this.state.activeBlock === "gold"
                  ? "bg-[#207af0] text-white transform -translate-y-2 shadow-2xl border-[#207af0]"
                  : "bg-white text-gray-900 shadow-lg border-gray-200 hover:shadow-xl hover:-translate-y-1"
                  } border-2 rounded-2xl p-4 sm:p-6 cursor-pointer transition-all duration-300 relative w-full`}
                onClick={() => {
                  this.setState({ activeBlock: "gold" });
                }}
              >
                <div className="text-center">
                  <h6
                    className={`text-left text-lg font-semibold mb-2 ${this.state.activeBlock === "gold"
                      ? "text-white"
                      : "text-gray-900"
                      }`}
                  >
                    Gold
                  </h6>
                  {/* <div
                    className={`text-lg sm:text-lg lg:text-lg font-bold mb-2 ${this.state.activeBlock === "gold"
                      ? "text-white"
                      : "#207af0"
                      }`}
                  >
                    <span className="inline">
                      {this.state.planSize === "quarterly"
                        ? "40,000 INR"
                        : "10,000 INR"}
                    </span>
                    <span className=" inline text-lg sm:text-lg font-bold">
                      {this.state.planSize === "quarterly"
                        ? "/quarter"
                        : "/month"}
                    </span>
                  </div> */}
                  <div className="flex space-x-1 items-start">
                    <span className={`text-[25px] font-semibold ${this.state.activeBlock !== "silver" ? "text-[#ffffff]" : "text-[#1780fb]"}`}>Rs</span>
                    <span className={`text-[55px] font-bold ${this.state.activeBlock !== "silver" ? "text-[#ffffff]" : "text-[#1780fb]"} leading-none`}>{this.state.planSize === "quarterly" ? 3000 : 799}</span>
                    <span className={`text-[25px] font-semibold ${this.state.activeBlock !== "silver" ? "text-[#ffffff]" : "text-[#1780fb]"}`}>.00</span>
                  </div>
                  <div className="flex flex-col items-end">
                    <div className="relative w-fit inline-block">
                      <div className="flex items-center space-x-1">
                        <span className={`text-[20px] font-semibold ${this.state.activeBlock !== "silver" ? "text-[#ffffff]" : ""}`}>{this.state.planSize === "quarterly" ? 4999 : 1399}</span>
                        <span className={`text-[20px] font-semibold ${this.state.activeBlock !== "silver" ? "text-[#ffffff]" : ""}`}>Rs</span>
                        <span className="text-[20px] text-red-500">*</span>
                      </div>

                      {/* perfect center strike for 20px text */}
                      <span
                        className="absolute left-0 right-0
											 top-1/2 -translate-y-1/2
											  h-[3px] bg-[#ff7a7a]
											   rotate-[-10deg] rounded-full"
                      ></span>
                    </div>
                    <span className={`text-[14px] ${this.state.activeBlock !== "silver" ? "text-[#ffffff]" : ""}`}>
                      / user / {this.state.planSize === "quarterly" ? "quarter" : "month"}
                    </span>
                  </div>
                  <p
                    className={`text-left text-sm mb-6 ${this.state.activeBlock === "gold"
                      ? "text-blue-100"
                      : "text-gray-600"
                      }`}
                  >
                    Billed{" "}
                    {this.state.planSize === "yearly" ? "Yearly" : "Quarterly"}
                  </p>
                </div>

                <ul className="space-y-3 mb-8">
                  {/* {this.renderBullet("Upto 10 Users", this.state.activeBlock === "gold")} */}
                  {this.renderBullet("Manage multiple stores", this.state.activeBlock === "gold")}
                  {this.renderBullet("Advanced Analytics", this.state.activeBlock === "gold")}
                  {this.renderBullet("Create Bill of Materials", this.state.activeBlock === "gold")}
                  {this.renderBullet("Manage Production", this.state.activeBlock === "gold")}
                  {this.renderBullet("Additional Silver Plan Features", this.state.activeBlock === "gold")}
                </ul>
                <Button
                  type={
                    this.state.activeBlock === "gold" ? "default" : "primary"
                  }
                  className="w-full h-12 rounded-full font-medium transition-all"
                  onClick={() => {
                    window.location.href = "/requestdemo";
                  }}
                >
                  Request a demo
                </Button>
              </div>

            </div>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="bg-white py-3 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">   {/* increased width */}
            <div className="bg-gradient-to-r from-yellow-50 via-pink-50 to-purple-50 rounded-2xl p-6 sm:p-6 text-center">
              <h3 className="text-[22px] sm:text-[27px] font-semibold text-gray-900 mb-1">
                Ready To Transform Your Business?
              </h3>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto text-base sm:text-base">
                Discover how Ease Margin is helping companies simplify operations and grow smarter.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center max-w-lg sm:max-w-2xl mx-auto w-full">
                <Input
                  placeholder="Your Email Address"
                  className="flex-1 h-8 rounded-md text-sm"
                  required
                  type="email"
                  value={this.state.email}
                  onChange={(e) => this.setState({ email: e.target.value })}
                />
                <Button
                  type="primary"
                  className="h-8 px-6 rounded-md font-medium text-sm min-w-[120px]"
                  onClick={this.handleNewsletterSubscribe}
                  loading={this.state.loading}
                >
                  Subscribe
                </Button>
              </div>
            </div>
          </div>
        </div>


      </>
    );
  }
}

const mapStateToProps = (state: any) => ({
  newsLettersData: state.api.getNewsLetters?.data?.data,
  addNewsLettersState: state.api?.addNewsLetters,
});

const mapDispatchToProps = (dispatch: any) => ({
  addNewsLetters: (payload: any) => dispatch(addNewsLetters(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Features);
